import { useState } from "react";
import InputTextBox from "../../../../widgets/InputTextBox";

const books = [
  {
    id: 1,
    title: "The Midnight Library",
    author: "Matt Haig",
    genre: "Literary Fiction",
    year: 2020,
    rating: 4.2,
    pages: 304,
    status: "Available",
    cover: "https://covers.openlibrary.org/b/id/10521270-L.jpg",
    description:
      "Between life and death there is a library, and within that library, the shelves go on forever.",
    tags: ["Fiction", "Fantasy", "Self-Help"],
  },
  {
    id: 2,
    title: "Atomic Habits",
    author: "James Clear",
    genre: "Self-Development",
    year: 2018,
    rating: 4.8,
    pages: 320,
    status: "Checked Out",
    cover: "https://covers.openlibrary.org/b/id/10261183-L.jpg",
    description:
      "An easy and proven way to build good habits and break bad ones using tiny changes.",
    tags: ["Habits", "Psychology", "Productivity"],
  },
  {
    id: 3,
    title: "Sapiens",
    author: "Yuval Noah Harari",
    genre: "History",
    year: 2011,
    rating: 4.5,
    pages: 443,
    status: "Available",
    cover: "https://covers.openlibrary.org/b/id/8739161-L.jpg",
    description:
      "A brief history of humankind — from the Stone Age to the Silicon Age.",
    tags: ["History", "Anthropology", "Science"],
  },
  {
    id: 4,
    title: "Dune",
    author: "Frank Herbert",
    genre: "Science Fiction",
    year: 1965,
    rating: 4.7,
    pages: 688,
    status: "Available",
    cover: "https://covers.openlibrary.org/b/id/10520095-L.jpg",
    description:
      "Set in the distant future amidst a feudal interstellar society, it tells the story of young Paul Atreides.",
    tags: ["Sci-Fi", "Classic", "Epic"],
  },
  {
    id: 5,
    title: "The Alchemist",
    author: "Paulo Coelho",
    genre: "Philosophical Fiction",
    year: 1988,
    rating: 4.1,
    pages: 208,
    status: "Reserved",
    cover: "https://covers.openlibrary.org/b/id/8701038-L.jpg",
    description:
      "A novel about a young Andalusian shepherd in his journey to the pyramids of Egypt.",
    tags: ["Philosophy", "Adventure", "Spirituality"],
  },
  {
    id: 6,
    title: "Project Hail Mary",
    author: "Andy Weir",
    genre: "Science Fiction",
    year: 2021,
    rating: 4.9,
    pages: 476,
    status: "Available",
    cover: "https://covers.openlibrary.org/b/id/10527843-L.jpg",
    description:
      "A lone astronaut must save the earth from disaster in this incredible feat of imagination.",
    tags: ["Sci-Fi", "Space", "Thriller"],
  },
];

// Genre accent: top strip color only (subtle border-left for detail panel)
const genreAccent = {
  "Literary Fiction": "bg-gray-700",
  "Self-Development": "bg-gray-600",
  History: "bg-gray-500",
  "Science Fiction": "bg-gray-800",
  "Philosophical Fiction": "bg-gray-700",
};

const genreBadge = {
  "Literary Fiction": "bg-gray-100 text-gray-700 border border-gray-300",
  "Self-Development": "bg-gray-100 text-gray-700 border border-gray-300",
  History: "bg-gray-100 text-gray-700 border border-gray-300",
  "Science Fiction": "bg-gray-100 text-gray-700 border border-gray-300",
  "Philosophical Fiction": "bg-gray-100 text-gray-700 border border-gray-300",
};

const statusConfig = {
  Available: {
    badge: "bg-green-50 text-green-700 border border-green-200",
    dot: "bg-green-500",
  },
  "Checked Out": {
    badge: "bg-amber-50 text-amber-700 border border-amber-200",
    dot: "bg-amber-500",
  },
  Reserved: {
    badge: "bg-blue-50 text-blue-700 border border-blue-200",
    dot: "bg-blue-500",
  },
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <svg key={s} className="w-3.5 h-3.5" viewBox="0 0 14 14" fill="none">
          <polygon
            points="7,1 8.8,5.3 13.5,5.7 10.1,8.7 11.1,13.3 7,10.8 2.9,13.3 3.9,8.7 0.5,5.7 5.2,5.3"
            fill={s <= Math.round(rating) ? "#f59e0b" : "#e5e7eb"}
          />
        </svg>
      ))}
      <span className="text-gray-400 text-xs ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

function BookCard({ book, onClick, isSelected }) {
  const accentStrip = genreAccent[book.genre] || "bg-gray-400";
  const badgeCls = genreBadge[book.genre] || "bg-gray-100 text-gray-600 border border-gray-200";
  const sc = statusConfig[book.status];

  return (
    <div
      onClick={() => onClick(book)}
      className={`
        bg-white rounded-2xl overflow-hidden cursor-pointer
        transition-all duration-200 flex flex-col
        border
        ${isSelected
          ? "border-gray-400 shadow-xl -translate-y-1"
          : "border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-gray-300"
        }
      `}
    >
      {/* Top color strip */}
      <div className={`h-1.5 w-full ${accentStrip}`} />

      <div className="p-5 flex flex-col flex-1">
        {/* Header badges */}
        <div className="flex justify-between items-start mb-3">
          <span className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md ${badgeCls}`}>
            {book.genre}
          </span>
          <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md ${sc.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full inline-block ${sc.dot}`} />
            {book.status}
          </span>
        </div>

        {/* Title & Author */}
        <h3 className="text-gray-900 font-bold text-base leading-snug mb-1" style={{ fontFamily: "Georgia, serif" }}>
          {book.title}
        </h3>
        <p className="text-gray-400 text-sm mb-3">
          by {book.author} · {book.year}
        </p>

        {/* Description */}
        <p className="text-gray-500 text-sm leading-relaxed mb-3 line-clamp-2">
          {book.description}
        </p>

        <StarRating rating={book.rating} />

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
          <div className="flex gap-1.5">
            {book.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="bg-gray-50 border border-gray-200 text-gray-500 text-xs px-2 py-0.5 rounded"
              >
                {tag}
              </span>
            ))}
          </div>
          <span className="text-xs text-gray-300">{book.pages} pp.</span>
        </div>
      </div>
    </div>
  );
}

export default function LibraryDeepSearchBookListView() {
  const [query, setQuery] = useState("");
  const [filterGenre, setFilterGenre] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [selected, setSelected] = useState(null);
  const [sortBy, setSortBy] = useState("title");

  const genres = ["All", ...new Set(books.map((b) => b.genre))];
  const statuses = ["All", "Available", "Checked Out", "Reserved"];

  const filtered = books
    .filter((b) => {
      const q = query.toLowerCase();
      return (
        (b.title.toLowerCase().includes(q) ||
          b.author.toLowerCase().includes(q) ||
          b.tags.some((t) => t.toLowerCase().includes(q))) &&
        (filterGenre === "All" || b.genre === filterGenre) &&
        (filterStatus === "All" || b.status === filterStatus)
      );
    })
    .sort((a, b) => {
      if (sortBy === "rating") return b.rating - a.rating;
      if (sortBy === "year") return b.year - a.year;
      if (sortBy === "pages") return a.pages - b.pages;
      return a.title.localeCompare(b.title);
    });

  const available = books.filter((b) => b.status === "Available").length;
  const avgRating = (books.reduce((s, b) => s + b.rating, 0) / books.length).toFixed(1);

  const selectBook = (b) => setSelected(selected?.id === b.id ? null : b);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">

      <div className="w-full mx-auto">
        {/* Search & Filters */}
        <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-7 flex flex-wrap gap-3 items-center shadow-sm">
          {/* Search */}
          <div className="flex-1 min-w-60 relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 text-sm">🔍</span>
            <input
              type="text"
              placeholder="Search title, author, or tag…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-300 outline-none focus:border-gray-400 focus:bg-white transition-colors"
            />
          </div>

          {/* Genre filter */}
           <select
            value={filterGenre}
            onChange={(e) => setFilterGenre(e.target.value)}
            className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer outline-none focus:border-gray-400 transition-colors"
          >
            {genres.map((g) => (
              <option key={g} value={g}>{g === "All" ? "Level" : g}</option>
            ))}
          </select>
           <select
            value={filterGenre}
            onChange={(e) => setFilterGenre(e.target.value)}
            className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer outline-none focus:border-gray-400 transition-colors"
          >
            {genres.map((g) => (
              <option key={g} value={g}>{g === "All" ? "Faculty" : g}</option>
            ))}
          </select>
          <select
            value={filterGenre}
            onChange={(e) => setFilterGenre(e.target.value)}
            className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer outline-none focus:border-gray-400 transition-colors"
          >
            {genres.map((g) => (
              <option key={g} value={g}>{g === "All" ? "Program" : g}</option>
            ))}
          </select>
          <select
            value={filterGenre}
            onChange={(e) => setFilterGenre(e.target.value)}
            className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer outline-none focus:border-gray-400 transition-colors"
          >
            {genres.map((g) => (
              <option key={g} value={g}>{g === "All" ? "Year Semester" : g}</option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer outline-none focus:border-gray-400 transition-colors"
          >
            {statuses.map((s) => (
              <option key={s} value={s}>{s === "All" ? "All Status" : s}</option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 cursor-pointer outline-none focus:border-gray-400 transition-colors"
          >
            <option value="title">Sort: Title</option>
            <option value="rating">Sort: Rating</option>
            <option value="year">Sort: Newest</option>
            <option value="pages">Sort: Shortest</option>
          </select>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-7">
          {[
            { label: "Total Books", value: books.length, border: "border-t-gray-700", num: "text-gray-800" },
            { label: "Available", value: available, border: "border-t-green-500", num: "text-green-600" },
            {
              label: "Checked Out",
              value: books.filter((b) => b.status === "Checked Out").length,
              border: "border-t-amber-400",
              num: "text-amber-600",
            },
            { label: "Avg Rating", value: `${avgRating}★`, border: "border-t-gray-400", num: "text-gray-700" },
          ].map(({ label, value, border, num }) => (
            <div
              key={label}
              className={`bg-white border border-gray-200 border-t-4 ${border} rounded-xl px-5 py-4 shadow-sm`}
            >
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-2">{label}</p>
              <p className={`text-2xl font-extrabold ${num}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Main layout */}
        <div className={`grid gap-6 items-start ${selected ? "grid-cols-[1fr_320px]" : "grid-cols-1"}`}>

          {/* Book grid */}
          <div>
            {filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-300">
                <p className="text-5xl mb-3">📭</p>
                <p className="text-lg font-semibold text-gray-400 mb-1">No books found</p>
                <p className="text-sm text-gray-300">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-4">
                {filtered.map((book) => (
                  <BookCard
                    key={book.id}
                    book={book}
                    onClick={selectBook}
                    isSelected={selected?.id === book.id}
                  />
                ))}
              </div>
            )}
          </div>

        
        </div>

      
      </div>
    </div>
  );
}